import {
    Component,
    DestroyRef,
    EventEmitter,
    Input,
    Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { filter } from 'rxjs';
import { MatButtonAvailableType } from '../../enums/mat-button-types';
import { MatDialogWidth } from '../../enums/mat-dialog-width';
import {
    AddCommentDialogModel,
    AddCommentDialogReceiveModel,
} from '../../models/add-comment-dialog-model';
import { SharedDialogService } from '../../services/shared-dialog.service';
import { AddCommentDialogComponent } from '../add-comment-dialog/add-comment-dialog.component';

@Component({
    selector: 'apcam-add-comment',
    templateUrl: './add-comment.component.html',
    styleUrls: ['./add-comment.component.scss'],
    imports: [MatButtonModule, MatIconModule],
})
export class AddCommentComponent {
    // #region Component I/O
    /**
     * Angular material color
     *
     * Can choose from the following color themes:  'primary' | 'accent' | 'warn'
     *
     * Default: 'primary'
     * */
    @Input()
    AMColor: 'primary' | 'accent' | 'warn' = 'primary';

    /**
     * Required
     *
     * Aria-label property for the main button, which opens the confirmation
     *
     * This property must accept a value which utilizes the multilinguality (e.g. ngx-translate)
     */
    @Input()
    ariaLabelButton: string = '';

    /**
     * CSS class for the main button, which opens the confirmation
     */
    @Input()
    buttonClass: string = '';

    /**
     * Text inside the main button, which opens the confirmation
     */
    @Input()
    buttonText: string = '';
    /**
     * Type of the button.
     *
     * You can choose any button type as defined by Angular material.
     *
     * See the 'MatButtonAvailableTypes' enum
     *
     * Default: MatButtonAvailableType.FLAT
     */
    @Input()
    buttonType: MatButtonAvailableType = MatButtonAvailableType.FLAT;

    /**
     * Placeholder text inside a textarea
     */
    @Input()
    commentPlaceholder: string = '';

    /**
     * Required
     *
     * Description of the confirm confirmation
     */
    @Input()
    detail: string = '';

    /**
     * Whether the main button is disabled
     */
    @Input()
    disabled: boolean = false;

    /**
     * Whether the main button is extended
     *
     * Has effect only if the 'buttonType' property is set to "FAB"
     */
    @Input()
    fabExtended: boolean = false;

    /**
     * Height of the modal
     */
    @Input()
    height: string = '';

    /**
     * Name of the icon to be displayed within the main button
     *
     * To display an icon, you need to set the 'withIcon' property as 'true'
     */
    @Input()
    iconName: string = '';

    /**
     * Label for the textarea
     */
    @Input()
    inputLabel: string = '';

    /**
     * Max height of the confirmation
     */
    @Input()
    maxHeight: string = '';

    /**
     * Max width of the confirmation
     */
    @Input()
    maxWidth: string = '';

    /**
     * Min height of the confirmation
     */
    @Input()
    minHeight: string = '';

    /**
     * Min width of the confirmation
     */
    @Input()
    minWidth: string = '';

    /**
     * Text inside the button which submits the comment
     */
    @Input()
    submitBtnText: string = '';

    /**
     * Required
     *
     * Summary of the confirm confirmation
     */
    @Input()
    summary: string = '';

    /**
     * Width of the confirmation
     */
    @Input()
    width: MatDialogWidth = MatDialogWidth.XS;

    /**
     * Whether display an icon inside the main button
     */
    @Input()
    withIcon: boolean = false;

    @Output()
    submit: EventEmitter<AddCommentDialogReceiveModel> =
        new EventEmitter<AddCommentDialogReceiveModel>();
    //#endregion

    MatButtonAvailableType = MatButtonAvailableType;

    constructor(
        private readonly destroyRef: DestroyRef,
        private readonly dialogService: SharedDialogService
    ) {}

    showDialog() {
        const dialogData: AddCommentDialogModel = {
            commentPlaceholder: this.commentPlaceholder,
            detail: this.detail,
            inputLabel: this.inputLabel,
            submitBtnText: this.submitBtnText,
            summary: this.summary,
        };
        const dialogRef = this.dialogService.open<
            AddCommentDialogComponent,
            AddCommentDialogModel,
            AddCommentDialogReceiveModel
        >(AddCommentDialogComponent, {
            width: this.width,
            height: this.height,
            minWidth: this.minWidth,
            minHeight: this.minHeight,
            maxWidth: this.maxWidth,
            maxHeight: this.maxHeight,
            data: dialogData,
        });

        // React to comment submission
        dialogRef
            .afterClosed()
            .pipe(
                filter((data) => !!data),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (data: AddCommentDialogReceiveModel) => {
                    // Data submitted, otherwise just close the window
                    if (data) {
                        this.onSubmit(data);
                    }
                },
            });
    }

    onSubmit(data: AddCommentDialogReceiveModel) {
        this.submit.emit(data);
    }
}
